import React from "react";

import { cn } from "../lib/utils";
import { Dialog, DialogContent } from "./dialog";

interface CommandContextValue {
  search: string;
  setSearch: (value: string) => void;
  matchCount: number;
  setMatchCount: React.Dispatch<React.SetStateAction<number>>;
}

const CommandContext = React.createContext<CommandContextValue | null>(null);

function useCommandContext() {
  const ctx = React.useContext(CommandContext);
  if (!ctx) throw new Error("Command components must be used within <Command>");
  return ctx;
}

export function Command({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const [search, setSearch] = React.useState("");
  const [matchCount, setMatchCount] = React.useState(0);

  // Reset match count before each render cycle so children can re-tally
  React.useEffect(() => {
    setMatchCount(0);
  }, [search]);

  return (
    <CommandContext.Provider
      value={{ search, setSearch, matchCount, setMatchCount }}
    >
      <div
        className={cn(
          "bg-popover text-popover-foreground flex flex-col overflow-hidden rounded-md",
          className,
        )}
      >
        {children}
      </div>
    </CommandContext.Provider>
  );
}

export function CommandDialog({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0">
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export function CommandInput({
  placeholder,
  className,
}: {
  placeholder?: string;
  className?: string;
}) {
  const { search, setSearch } = useCommandContext();

  return (
    <div className="flex items-center border-b px-3">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      />
    </div>
  );
}

export function CommandList({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "max-h-[300px] overflow-x-hidden overflow-y-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CommandEmpty({ children }: { children: React.ReactNode }) {
  const { search, matchCount } = useCommandContext();
  // Show empty message only when there's a search query and no matches
  if (!search || matchCount > 0) return null;
  return (
    <div className="text-muted-foreground py-6 text-center text-sm">
      {children}
    </div>
  );
}

export function CommandGroup({
  heading,
  children,
}: {
  heading?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-foreground overflow-hidden p-1">
      {heading && (
        <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
          {heading}
        </div>
      )}
      {children}
    </div>
  );
}

export function CommandItem({
  className,
  onSelect,
  children,
}: {
  className?: string;
  onSelect?: () => void;
  children: React.ReactNode;
}) {
  const { search, setMatchCount } = useCommandContext();
  const ref = React.useRef<HTMLDivElement>(null);

  const textContent = React.useMemo(() => {
    // Extract text from children for filtering
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === "string") return node;
      if (typeof node === "number") return String(node);
      if (!node) return "";
      if (Array.isArray(node)) return node.map(extractText).join("");
      if (React.isValidElement(node) && node.props) {
        return extractText(
          (node.props as { children?: React.ReactNode }).children,
        );
      }
      return "";
    };
    return extractText(children).toLowerCase();
  }, [children]);

  const isMatch = !search || textContent.includes(search.toLowerCase());

  React.useEffect(() => {
    if (isMatch && search) {
      setMatchCount((c) => c + 1);
    }
  }, [isMatch, search, setMatchCount]);

  if (!isMatch) return null;

  return (
    <div
      ref={ref}
      className={cn(
        "hover:bg-accent hover:text-accent-foreground relative flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm outline-none select-none",
        className,
      )}
      onClick={onSelect}
      role="option"
      aria-selected={false}
    >
      {children}
    </div>
  );
}

export function CommandSeparator({ className }: { className?: string }) {
  return <hr className={cn("bg-border -mx-1 h-px border-0", className)} />;
}

export function CommandShortcut({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className,
      )}
    >
      {children}
    </span>
  );
}
